import { Typography, Button, Form } from "antd";
import { useCallback, useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import {
    deleteVehicle,
    setModalName,
    updateFirmwareVehicle,
    getDropdownFirmwares,
    updateFirmwareOnVehicles
} from "store/actions";
import { FormItem, SelectSearch } from "../components";
import { parseFirmwares } from "utils/parsers";
import { Formik } from "formik";

const { Text, Title } = Typography;

export default function BulkVehicleFirmwareUpdateForm() {
    const {
        firmwares: { dropdownFirmwares },
    } = useSelector<AppState, AppState>((state) => state);

    const dispatch = useDispatch();

    const { selectedVehicles } = useSelector<AppState, AppState["vehicles"]>(
        (state) => state.vehicles
    );

    const firmwaresOptions = useMemo(
        () => parseFirmwares(dropdownFirmwares),
        [dropdownFirmwares]
    );

    // Helper function to find matching option value from firmwaresOptions
    const findMatchingOptionValue = useCallback((versionString: string) => {
        if (!versionString || !firmwaresOptions || firmwaresOptions.length === 0) {
            return '';
        }

        // Find matching option - check value, label, and name properties
        const matchingOption = firmwaresOptions.find(
            (option: any) =>
                option.value === versionString ||
                option.label === versionString ||
                option.name === versionString ||
                // Also check if the label/name contains the version
                option.label?.includes(versionString) ||
                option.name?.includes(versionString) ||
                // Check string comparison for type mismatch
                String(option.value) === String(versionString) ||
                String(option.label) === String(versionString)
        );

        return matchingOption?.value || '';
    }, [firmwaresOptions]);

    // Determine the pre-selected firmware version based on selected vehicles
    const preSelectedFirmwareVersion = useMemo(() => {
        // Return empty if no vehicles selected or no options available
        if (!selectedVehicles || selectedVehicles.length === 0) {
            return '';
        }

        if (!firmwaresOptions || firmwaresOptions.length === 0) {
            return '';
        }

        // Get all firmware versions from selected vehicles
        const firmwareVersions = selectedVehicles
            .map(vehicle => vehicle?.firmwareVersion) // Adjust property name if needed
            .filter(version => version !== undefined && version !== null && version !== '');

        // Debug logs
        console.log("Selected Vehicles:", selectedVehicles);
        console.log("Firmware Versions from vehicles:", firmwareVersions);
        console.log("Firmware Options:", firmwaresOptions);

        // If no vehicles have firmware versions, return empty
        if (firmwareVersions.length === 0) {
            return '';
        }

        // If only one vehicle is selected and has a version, pre-select it
        if (selectedVehicles.length === 1 && firmwareVersions.length === 1) {
            const matchedValue = findMatchingOptionValue(firmwareVersions[0]);
            console.log("Single vehicle - Matched Value:", matchedValue);
            return matchedValue;
        }

        // Check if all selected vehicles have the same firmware version
        const firstVersion = firmwareVersions[0];
        const allSameVersion = firmwareVersions.every(version => version === firstVersion);

        // If all vehicles have the same version, pre-select it
        if (allSameVersion && firmwareVersions.length === selectedVehicles.length) {
            const matchedValue = findMatchingOptionValue(firstVersion);
            console.log("All same version - Matched Value:", matchedValue);
            return matchedValue;
        }

        // Different versions among selected vehicles, return empty
        return '';
    }, [selectedVehicles, firmwaresOptions, findMatchingOptionValue]);

    // Use the pre-selected version in initial values
    const initialValues = useMemo(() => ({
        firmwareVersion: preSelectedFirmwareVersion,
        'Firmware Version': preSelectedFirmwareVersion, // Also set with the fieldName key
    }), [preSelectedFirmwareVersion]);

    const devicesIotIdList: string[] = useMemo(() => {
        if (!selectedVehicles) {
            return [];
        }
        return selectedVehicles
            .map(item => item?.deviceIotId)
            .filter((devicesIotId): devicesIotId is string => typeof devicesIotId === 'string');
    }, [selectedVehicles]);

    const onSubmit = useCallback((values) => {
        const firmwareValue = values['Firmware Version'] || values.firmwareVersion;
        console.log("Version Value:", firmwareValue);
        
        if (!firmwareValue) {
            console.warn("No firmware version selected");
            return;
        }
        
        dispatch(updateFirmwareOnVehicles(firmwareValue.toString(), devicesIotIdList));
        onCancel();
    }, [dispatch, devicesIotIdList]);

    const onCancel = useCallback(() => {
        dispatch(setModalName(undefined));
    }, [dispatch]);

    useEffect(() => {
        if (!dropdownFirmwares) {
            dispatch(getDropdownFirmwares());
        }
    }, [dispatch, dropdownFirmwares]);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            enableReinitialize={true}
        >
            {({
                values,
                handleSubmit,
                setFieldValue,
            }) => (
                <section className="delete-modal">
                    <Title level={5}>Update Firmware</Title>
                    <Text type="secondary">
                        Please select the target firmware version for the selected vehicle(s):
                        <br /><br />
                    </Text>
                    <Form onFinish={handleSubmit} className="--update-firmware-form">
                        <FormItem label="Firmware Versions">
                            <SelectSearch
                                value={values['Firmware Version'] || values.firmwareVersion}
                                items={firmwaresOptions}
                                setFieldValue={setFieldValue}
                                fieldName="Firmware Version"
                                placeholder="Select"
                            />
                        </FormItem>
                        <div className="action-btn-container">
                            <Button className="cancel-btn" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button 
                                type="primary" 
                                danger 
                                htmlType="submit"
                                disabled={!values['Firmware Version'] && !values.firmwareVersion}
                            >
                                Update Firmware
                            </Button>
                        </div>
                    </Form>
                </section>
            )}
        </Formik>
    );
}